import { Avatar, Badge, Divider } from "antd";
import FeatherIcon from "feather-icons-react";
import { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Dropdown } from "../../dropdown/dropdown";
import Heading from "../../heading/heading";
import { Popover } from "../../popup/popup";
import { InfoWraper, NavAuth, UserDropDwon } from "./auth-info-style";
import { LanguagesApi, UsersApi } from "api/api";
import { TaskItemsApi } from "api/clients/task-items-api";
import { useTranslation } from "react-i18next";
import { theme } from "config/theme/themeVariables";
import { decodeJWT } from "utility/decode-jwt";
import { hasPermission } from "utility/accessibility/hasPermission";
import styled from "styled-components";
import { LocalStorageKeys } from "hooks/useLocalStorage";
import { useLocalStorage } from "hooks/useLocalStorage";

const languagesApi = new LanguagesApi();
const userApi = new UsersApi();
const taskItemsApi = new TaskItemsApi();

const UserInfo = styled.div`
  text-align: center;
  padding: 12px 0;
`;

const FullName = styled(Heading)`
  margin-bottom: 4px !important;
`;

const UserName = styled.div`
  color: #666;
  font-size: 13px;
  margin-bottom: 2px;
`;

const Organization = styled.div`
  color: #1890ff;
  font-size: 12px;
  font-weight: 500;
`;

function AuthInfo() {
	const navigate = useNavigate();

	const { i18n } = useTranslation();

	const [languages, setLanguages] = useState([]);
	const [user] = useLocalStorage(LocalStorageKeys.user, null);
	const userName = user?.userName;

	// Memoize token to prevent recreation on every render
	const token = useMemo(() => decodeJWT(), []);

	const [fullName] = useState(token?.fullName);
	const [organizationName] = useState(
		user?.organization?.name || token?.organizationName,
	);
	const [uncompletedTaskCount, setUncompletedTaskCount] = useState(0);

	// Extract userId once to avoid recreating on every render
	const userId = useMemo(() => {
		// Try various possible claim names for user ID
		const id =
			token?.sub ||
			token?.nameid ||
			token?.[
				"http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier"
			] ||
			token?.userId;

		console.log("Decoded JWT token:", token);
		console.log("Extracted userId:", id);
		return id;
	}, [token]);

	useEffect(() => {
		const getLanguages = async () => {
			const { data } = await languagesApi.apiLanguagesGet();
			const lng = localStorage.getItem("i18nextLng");

			setLanguages(data);

			if (lng === "en-US") {
				data?.forEach((res) => {
					if (res.isDefault) {
						i18n.changeLanguage(res.i18nCode.code);
					}
				});
			}
		};

		const fetchUncompletedTasks = async () => {
			if (!userId) return;

			try {
				console.log("Fetching tasks with assignedToId:", userId);

				// Fetch tasks assigned to current user that are not completed (status 0 or 1)
				const { data } = await taskItemsApi.apiTaskItemsGet({
					assignedToId: userId,
					pageNumber: 1,
					pageSize: 20,
				});

				console.log("API Response:", data);
				console.log("Total tasks returned:", data?.items?.length || 0);

				// Count tasks that are Open (0) or In Progress (1)
				const uncompletedCount =
					data?.items?.filter((task) => task.status === 0 || task.status === 1)
						.length || 0;

				console.log("Uncompleted tasks count:", uncompletedCount);
				setUncompletedTaskCount(uncompletedCount);
			} catch (error) {
				console.error("Error fetching tasks:", error);
				// Silently fail - task count is not critical
			}
		};

		getLanguages();
		// Only fetch tasks if user has permission to avoid 403 errors
		if (hasPermission("tasks:list")) {
			fetchUncompletedTasks();
		}
	}, [i18n, userId]); // Dependencies are now stable and properly tracked

	const SignOut = (e) => {
		e.preventDefault();
		localStorage.removeItem("user");
		navigate("/login");
	};

	const onFlagChangeHandle = (value) => {
		try {
			i18n.changeLanguage(value);
			const languageObj =
				languages.find((x) => x?.i18nCode?.code === value) || null;

			if (!languageObj) return;

			userApi.apiUsersSetDefaultLanguagePut({
				updateLanguageRequest: { languageId: languageObj?.id },
			});
		} catch {
			// Silently fail - language change is not critical
		}
	};

	const userContent = (
		<UserDropDwon>
			<div className="user-dropdwon">
				<UserInfo>
					<FullName as="h5">{fullName}</FullName>
					{fullName && <UserName>{userName}</UserName>}
					{organizationName && <Organization>{organizationName}</Organization>}
				</UserInfo>
				<Divider style={{ margin: 0 }} />
				<ul className="user-dropdwon__links">
					<li>
						<Link to="profile">
							<FeatherIcon icon="user" /> Profile
						</Link>
					</li>
				</ul>
				<Link className="user-dropdwon__bottomAction" onClick={SignOut}>
					<FeatherIcon icon="log-out" /> Sign Out
				</Link>
			</div>
		</UserDropDwon>
	);

	const country = (
		<NavAuth>
			{languages.map((language) => {
				const isDefaultLanguage = language?.i18nCode?.code === i18n?.language;
				return (
					<Link
						onClick={() => onFlagChangeHandle(language?.i18nCode?.code)}
						to="#"
						key={language?.i18nCode?.code}
						style={{
							backgroundColor: isDefaultLanguage ? theme.linkActive : "",
						}}
					>
						<span className={isDefaultLanguage ? "ant-menu-item-selected" : ""}>
							{language?.i18nCode?.name}
						</span>
					</Link>
				);
			})}
		</NavAuth>
	);

	return (
		<InfoWraper>
			{/* <Message /> */}
			{/* <Notification /> */}

			{/* <Settings /> */}
			{/* <Support /> */}
			{hasPermission("tasks:list") && (
				<div className="nav-author">
					<Link
						to="/tasks"
						className="head-example"
						style={{
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
							flexDirection: "row",
						}}
					>
						<Badge
							count={uncompletedTaskCount > 9 ? "9+" : uncompletedTaskCount}
							offset={[-2, 2]}
							size="small"
						>
							<FeatherIcon icon="check-square" size={24} />
						</Badge>
					</Link>
				</div>
			)}

			<div className="nav-author">
				<Dropdown placement="bottomRight" content={country}>
					<Link
						to="#"
						className="head-example"
						style={{
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
							flexDirection: "row",
						}}
					>
						<FeatherIcon icon="globe" />
						<p style={{ fontWeight: 500, margin: 0, paddingLeft: 5 }}>
							{localStorage.getItem("i18nextLng").toUpperCase()}
						</p>
					</Link>
				</Dropdown>
			</div>

			<div className="nav-author">
				<Popover placement="bottomRight" content={userContent} action="click">
					<Link to="#" className="head-example">
						<Avatar src="https://cdn0.iconfinder.com/data/icons/user-pictures/100/matureman1-512.png" />
					</Link>
				</Popover>
			</div>
		</InfoWraper>
	);
}

export default AuthInfo;
